import { create } from "zustand"

import { TaskViewScope } from "../constants"
import type { QualityApprovalSearchParams } from "../schemas"

import type {
  GetQualityApprovalListQuery,
  QualityApproval,
  QualityApprovalPendingSort,
} from "@/graphql/codegen/graphql"
import { GetQualityApprovalListDocument } from "@/graphql/codegen/graphql"
import { createSearchStore } from "@/stores/createSearchStore"

export type QualityApprovalSearchFilters = QualityApprovalSearchParams & { taskViewScope?: TaskViewScope }

interface QualityApprovalQueryInput {
  sort: QualityApprovalPendingSort
  offset: number
  limit: number
  isAssignedToMe: boolean
}

const deduplicateQualityApprovals = (
  newData: QualityApproval[],
  existingData: QualityApproval[]
): QualityApproval[] => {
  const uniqueIds = new Set(existingData.map((qualityApproval) => qualityApproval.qualityApprovalId))
  return [...existingData, ...newData.filter((qualityApproval) => !uniqueIds.has(qualityApproval.qualityApprovalId))]
}

export const useQualityApprovalSearchStore = create(
  createSearchStore<QualityApproval, QualityApprovalSearchFilters, GetQualityApprovalListQuery>({
    documentNode: GetQualityApprovalListDocument,
    deduplicateData: deduplicateQualityApprovals,
    getQueryInput: (
      filters: QualityApprovalSearchFilters,
      offset: number,
      limit: number
    ): QualityApprovalQueryInput => {
      const { field, order, taskViewScope } = filters
      return {
        sort: { field, order },
        offset,
        limit,
        isAssignedToMe: taskViewScope === TaskViewScope.MyTasks,
      }
    },
    getQueryVariables: ({ isAssignedToMe, ...input }: QualityApprovalQueryInput) => ({
      input,
      isAssignedToMe,
    }),
    extractDataFromResponse: (data) => {
      const output = data.dashboard.myQualityApprovalList ?? data.dashboard.qualityApprovalList
      return {
        __typename: output?.__typename ?? "QualityApprovalListFailure",
        data: output?.__typename === "QualityApprovalListSuccess" ? (output.qualityApprovals as QualityApproval[]) : [],
        error: output?.__typename === "QualityApprovalListFailure" ? output.error : undefined,
      }
    },
  })
)
